// Mock data for DataOps API endpoints
// This file contains comprehensive mock data for all dataops endpoints
import { PROJECT_AI_TRANSFORM_ID, PROJECT_CLOUD_MIGRATION_ID, PROJECT_DATA_LAKE_ID, WORKLOAD_AI_PROD_ID, WORKLOAD_NON_PROD_ID, WORKLOAD_DATA_PIPELINE_ID } from './mockData';

const PROJECT_TO_WORKLOAD: Record<string, { id: string; name: string }> = {
  [PROJECT_AI_TRANSFORM_ID]: { id: WORKLOAD_AI_PROD_ID, name: 'Marketing AI Platform' },
  [PROJECT_CLOUD_MIGRATION_ID]: { id: WORKLOAD_NON_PROD_ID, name: 'Financial Data Pipeline' },
  [PROJECT_DATA_LAKE_ID]: { id: WORKLOAD_DATA_PIPELINE_ID, name: 'Legacy Application' },
};

const PROJECT_TO_ACCOUNT: Record<string, string> = {
  [PROJECT_AI_TRANSFORM_ID]: '123456789012',
  [PROJECT_CLOUD_MIGRATION_ID]: '987654321098',
  [PROJECT_DATA_LAKE_ID]: '123456789012',
};

// Distribute items across environments based on index for variety
const ALL_ENVIRONMENTS = [
  'env-0000-0000-0000-000000000001', // Production
  'env-0000-0000-0000-000000000002', // Staging
  'env-0000-0000-0000-000000000003', // Development
  'env-0000-0000-0000-000000000004', // QA
];

// Enrich any item that has project_id with workload + account + environment fields
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function enrichWithWorkload<T extends Record<string, any>>(items: T[]): T[] {
  return items.map((item, index) => {
    const pid = item.project_id as string | undefined;
    const wl = pid ? PROJECT_TO_WORKLOAD[pid] : undefined;
    const accountId = pid ? PROJECT_TO_ACCOUNT[pid] : undefined;
    // Distribute across environments: prod items get prod/staging, dev items get dev/qa
    const envIndex = accountId === '123456789012'
      ? (index % 2 === 0 ? 0 : 1)   // Production or Staging
      : (index % 2 === 0 ? 2 : 3);  // Development or QA
    return {
      ...item,
      ...(wl ? { workload_id: wl.id, workload_name: wl.name } : {}),
      ...(accountId ? { account_id: accountId, cloud_account_id: accountId } : {}),
      ...(pid ? { environment_id: ALL_ENVIRONMENTS[envIndex] } : {}),
    };
  });
}

// ==================== Catalog Mock Data ====================

export const mockCatalogOverview = {
  total_datasets: 42,
  total_jobs: 18,
  active_jobs: 5,
  namespaces_count: 6,
  quality_score: 87.5,
  degraded_datasets: 1,
};

export const mockDatasets = enrichWithWorkload([
  {
    id: 'ds-critical-001',
    name: 'customer-interactions-v3',
    description: 'Customer interaction history feeding AI recommendation engine — stale since pipeline failure',
    namespace: 'ai-data',
    source: 'redshift',
    fields_count: 28,
    quality_score: 64.2,
    freshness_status: 'stale',
    freshness_sla_hours: 6,
    freshness_actual_hours: 36,
    last_updated: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
    tags: ['ai-critical', 'customer-data', 'pii'],
    project_id: PROJECT_AI_TRANSFORM_ID,
  },
  {
    id: 'ds-001',
    name: 'customer_profiles',
    description: 'Customer profile data with demographics and preferences',
    namespace: 'analytics',
    source: 'postgresql',
    fields_count: 24,
    quality_score: 95,
    last_updated: '2024-01-15T10:30:00Z',
    tags: ['customer', 'profile', 'analytics'],
    project_id: PROJECT_CLOUD_MIGRATION_ID,
  },
  {
    id: 'ds-002',
    name: 'order_transactions',
    description: 'E-commerce order and transaction records',
    namespace: 'commerce',
    source: 'mysql',
    fields_count: 18,
    quality_score: 92,
    last_updated: '2024-01-15T09:15:00Z',
    tags: ['orders', 'transactions', 'commerce'],
    project_id: PROJECT_DATA_LAKE_ID,
  },
  {
    id: 'ds-003',
    name: 'product_catalog',
    description: 'Product information and inventory data',
    namespace: 'commerce',
    source: 'mongodb',
    fields_count: 32,
    quality_score: 88,
    last_updated: '2024-01-14T16:45:00Z',
    tags: ['products', 'inventory', 'catalog'],
    project_id: PROJECT_CLOUD_MIGRATION_ID,
  },
  {
    id: 'ds-004',
    name: 'user_sessions',
    description: 'User session tracking and analytics',
    namespace: 'analytics',
    source: 's3',
    fields_count: 15,
    quality_score: 85,
    last_updated: '2024-01-15T11:20:00Z',
    tags: ['sessions', 'analytics', 'tracking'],
    project_id: PROJECT_AI_TRANSFORM_ID,
  },
  {
    id: 'ds-005',
    name: 'marketing_campaigns',
    description: 'Marketing campaign performance metrics',
    namespace: 'marketing',
    source: 'snowflake',
    fields_count: 20,
    quality_score: 90,
    last_updated: '2024-01-15T08:00:00Z',
    tags: ['marketing', 'campaigns', 'metrics'],
    project_id: PROJECT_DATA_LAKE_ID,
  },
  {
    id: 'ds-006',
    name: 'financial_reports',
    description: 'Financial reporting and accounting data',
    namespace: 'finance',
    source: 'redshift',
    fields_count: 28,
    quality_score: 98,
    last_updated: '2024-01-15T12:00:00Z',
    tags: ['finance', 'reports', 'accounting'],
    project_id: PROJECT_CLOUD_MIGRATION_ID,
  },
  {
    id: 'ds-007',
    name: 'ml_training_features',
    description: 'Feature store for ML model training pipelines',
    namespace: 'ai-data',
    source: 's3',
    fields_count: 45,
    quality_score: 91,
    last_updated: '2024-01-15T14:30:00Z',
    tags: ['ml', 'features', 'training'],
    project_id: PROJECT_AI_TRANSFORM_ID,
  },
  {
    id: 'ds-008',
    name: 'inference_logs',
    description: 'Model inference request and response logs',
    namespace: 'ai-data',
    source: 'kinesis',
    fields_count: 22,
    quality_score: 87,
    last_updated: '2024-01-15T15:00:00Z',
    tags: ['inference', 'logs', 'ai'],
    project_id: PROJECT_AI_TRANSFORM_ID,
  },
  {
    id: 'ds-009',
    name: 'data_lake_raw_events',
    description: 'Raw event stream ingestion for analytics',
    namespace: 'analytics',
    source: 's3',
    fields_count: 35,
    quality_score: 82,
    last_updated: '2024-01-15T13:45:00Z',
    tags: ['events', 'raw', 'ingestion'],
    project_id: PROJECT_DATA_LAKE_ID,
  },
  {
    id: 'ds-010',
    name: 'migration_audit_trail',
    description: 'Audit log of cloud migration activities and changes',
    namespace: 'analytics',
    source: 'postgresql',
    fields_count: 18,
    quality_score: 96,
    last_updated: '2024-01-15T09:00:00Z',
    tags: ['audit', 'migration', 'compliance'],
    project_id: PROJECT_CLOUD_MIGRATION_ID,
  },
]);

export const mockJobs = enrichWithWorkload([
  {
    id: 'job-001',
    name: 'customer_data_pipeline',
    description: 'ETL pipeline for customer profile data',
    namespace: 'analytics',
    status: 'running',
    last_run_at: '2024-01-15T10:00:00Z',
    tags: ['etl', 'customer', 'daily'],
    project_id: PROJECT_AI_TRANSFORM_ID,
  },
  {
    id: 'job-002',
    name: 'order_processing',
    description: 'Process and validate order transactions',
    namespace: 'commerce',
    status: 'success',
    last_run_at: '2024-01-15T09:30:00Z',
    tags: ['orders', 'processing', 'validation'],
    project_id: PROJECT_CLOUD_MIGRATION_ID,
  },
  {
    id: 'job-003',
    name: 'product_sync',
    description: 'Synchronize product catalog from external sources',
    namespace: 'commerce',
    status: 'success',
    last_run_at: '2024-01-15T08:15:00Z',
    tags: ['products', 'sync', 'hourly'],
    project_id: PROJECT_DATA_LAKE_ID,
  },
  {
    id: 'job-004',
    name: 'session_aggregation',
    description: 'Aggregate user session data for analytics',
    namespace: 'analytics',
    status: 'running',
    last_run_at: '2024-01-15T11:00:00Z',
    tags: ['sessions', 'aggregation', 'analytics'],
    project_id: PROJECT_AI_TRANSFORM_ID,
  },
  {
    id: 'job-005',
    name: 'campaign_metrics',
    description: 'Calculate marketing campaign performance metrics',
    namespace: 'marketing',
    status: 'pending',
    last_run_at: '2024-01-15T07:00:00Z',
    tags: ['marketing', 'metrics', 'daily'],
    project_id: PROJECT_DATA_LAKE_ID,
  },
  {
    id: 'job-006',
    name: 'financial_reconciliation',
    description: 'Reconcile financial data and generate reports',
    namespace: 'finance',
    status: 'failed',
    last_run_at: '2024-01-15T06:00:00Z',
    tags: ['finance', 'reconciliation', 'reports'],
    project_id: PROJECT_CLOUD_MIGRATION_ID,
  },
  {
    id: 'job-ci-refresh',
    name: 'customer_interactions_refresh',
    description: 'ETL pipeline refreshing customer-interactions-v3 from Redshift source (6h SLA)',
    namespace: 'ai-data',
    status: 'failed',
    last_run_at: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
    tags: ['etl', 'ai-critical', 'customer-data', '6h-sla'],
    project_id: PROJECT_AI_TRANSFORM_ID,
  },
  {
    id: 'job-007',
    name: 'ml_feature_engineering',
    description: 'Feature engineering pipeline for ML models',
    namespace: 'ai-data',
    status: 'success',
    last_run_at: '2024-01-15T12:00:00Z',
    tags: ['ml', 'features', 'engineering'],
    project_id: PROJECT_AI_TRANSFORM_ID,
  },
  {
    id: 'job-008',
    name: 'data_lake_compaction',
    description: 'S3 data lake file compaction and optimization',
    namespace: 'analytics',
    status: 'success',
    last_run_at: '2024-01-15T05:00:00Z',
    tags: ['compaction', 'optimization', 's3'],
    project_id: PROJECT_DATA_LAKE_ID,
  },
  {
    id: 'job-009',
    name: 'migration_validation',
    description: 'Validate migrated resources and data integrity',
    namespace: 'analytics',
    status: 'success',
    last_run_at: '2024-01-15T04:30:00Z',
    tags: ['migration', 'validation', 'integrity'],
    project_id: PROJECT_CLOUD_MIGRATION_ID,
  },
]);

export const mockNamespaces = [
  { id: 'ns-001', name: 'analytics' },
  { id: 'ns-002', name: 'commerce' },
  { id: 'ns-003', name: 'marketing' },
  { id: 'ns-004', name: 'finance' },
  { id: 'ns-005', name: 'operations' },
  { id: 'ns-006', name: 'security' },
];

// ==================== Data Quality Mock Data ====================

export const mockQualityResults: Record<string, any[]> = {
  'ds-001': [
    {
      id: 'dq-001',
      dataset_id: 'ds-001',
      client_id: 'client-001',
      score: 95,
      rules_passed: 6,
      rules_failed: 1,
      rules_total: 7,
      rule_results: [
        { name: 'RowCount', description: 'RowCount > 0', result: 'PASS', evaluation_message: 'Row count is 15432' },
        { name: 'IsComplete "customer_id"', description: 'IsComplete "customer_id"', result: 'PASS', evaluation_message: '100% complete' },
        { name: 'IsUnique "customer_id"', description: 'IsUnique "customer_id"', result: 'PASS', evaluation_message: 'All values are unique' },
        { name: 'IsComplete "email"', description: 'IsComplete "email"', result: 'PASS', evaluation_message: '100% complete' },
        { name: 'IsComplete "name"', description: 'IsComplete "name"', result: 'PASS', evaluation_message: '100% complete' },
        { name: 'IsComplete "phone"', description: 'IsComplete "phone"', result: 'FAIL', evaluation_message: '92% complete (8% null)' },
        { name: 'DataFreshness "updated_at"', description: 'DataFreshness "updated_at" <= 30 days', result: 'PASS', evaluation_message: 'Data is 2 days old' },
      ],
      glue_result_id: 'dq-result-abc123',
      glue_run_id: 'dq-run-abc123',
      status: 'completed',
      error_message: null,
      started_at: '2026-02-19T10:00:00Z',
      completed_at: '2026-02-19T10:03:45Z',
      created_at: '2026-02-19T10:00:00Z',
    },
  ],
  'ds-002': [
    {
      id: 'dq-002',
      dataset_id: 'ds-002',
      client_id: 'client-001',
      score: 92,
      rules_passed: 6,
      rules_failed: 1,
      rules_total: 7,
      rule_results: [
        { name: 'RowCount', description: 'RowCount > 0', result: 'PASS', evaluation_message: 'Row count is 8921' },
        { name: 'IsComplete "order_id"', description: 'IsComplete "order_id"', result: 'PASS', evaluation_message: '100% complete' },
        { name: 'IsUnique "order_id"', description: 'IsUnique "order_id"', result: 'PASS', evaluation_message: 'All values are unique' },
        { name: 'IsComplete "amount"', description: 'IsComplete "amount"', result: 'PASS', evaluation_message: '100% complete' },
        { name: 'ColumnValues "amount"', description: 'ColumnValues "amount" >= 0', result: 'PASS', evaluation_message: 'All values >= 0' },
        { name: 'IsComplete "discount_code"', description: 'IsComplete "discount_code"', result: 'FAIL', evaluation_message: '78% complete' },
        { name: 'DataFreshness "created_at"', description: 'DataFreshness "created_at" <= 30 days', result: 'PASS', evaluation_message: 'Data is 5 days old' },
      ],
      glue_result_id: 'dq-result-def456',
      glue_run_id: 'dq-run-def456',
      status: 'completed',
      error_message: null,
      started_at: '2026-02-18T14:00:00Z',
      completed_at: '2026-02-18T14:02:30Z',
      created_at: '2026-02-18T14:00:00Z',
    },
  ],
};

// ==================== Lineage Mock Data ====================
// Story: customer-interactions-v3 feeds into AI feature pipeline,
// which feeds a GenAI prompt and an agent workflow (Act 3, Step 7)

export const mockLineageGraph = {
  nodes: [
    { id: 'ds-critical-001', node_id: 'ds-critical-001', name: 'customer-interactions-v3', node_type: 'dataset', namespace: 'ai-data' },
    { id: 'ds-002', node_id: 'ds-002', name: 'order_transactions', node_type: 'dataset', namespace: 'commerce' },
    { id: 'job-ai-feat', node_id: 'job-ai-feat', name: 'ai_feature_pipeline', node_type: 'job', namespace: 'ai-data' },
    { id: 'prompt-001', node_id: 'prompt-001', name: 'product-recommendation', node_type: 'prompt', namespace: 'genaiops' },
    { id: 'agent-001', node_id: 'agent-001', name: 'customer-support-agent', node_type: 'agent', namespace: 'agentops' },
  ],
  edges: [
    { source_node_id: 'ds-critical-001', target_node_id: 'job-ai-feat' },
    { source_node_id: 'ds-002', target_node_id: 'job-ai-feat' },
    { source_node_id: 'job-ai-feat', target_node_id: 'prompt-001' },
    { source_node_id: 'job-ai-feat', target_node_id: 'agent-001' },
  ],
};

export const mockUpstreamNodes = [
  { id: 'ds-critical-001', name: 'customer-interactions-v3', node_type: 'dataset', namespace: 'ai-data' },
  { id: 'ds-002', name: 'order_transactions', node_type: 'dataset', namespace: 'commerce' },
];

export const mockDownstreamNodes = [
  { id: 'prompt-001', name: 'product-recommendation', node_type: 'prompt', namespace: 'genaiops' },
  { id: 'agent-001', name: 'customer-support-agent', node_type: 'agent', namespace: 'agentops' },
];

export const mockLineagePath = {
  nodes: [
    { id: 'ds-critical-001', name: 'customer-interactions-v3', node_type: 'dataset' },
    { id: 'job-ai-feat', name: 'ai_feature_pipeline', node_type: 'job' },
    { id: 'prompt-001', name: 'product-recommendation', node_type: 'prompt' },
  ],
  hops: 2,
};

// ==================== Runs Mock Data ====================

export const mockRunsOverview = {
  total_runs: 159,
  success_rate: 86.2,
  successful_runs: 137,
  failed_runs: 13,
  running_runs: 5,
  pending_runs: 3,
  completed: 137,
  failed: 13,
  running: 5,
  pending: 3,
};

export const mockRuns = enrichWithWorkload([
  {
    id: 'run-ci-fail',
    job_id: 'job-ci-refresh',
    status: 'failed',
    started_at: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 36 * 60 * 60 * 1000 + 45000).toISOString(),
    duration_seconds: 45,
    error_message: 'Source database connection refused: Redshift cluster ai-analytics-prod endpoint unreachable (connection timeout after 30s). Pipeline cannot refresh customer-interactions-v3.',
  },
  {
    id: 'run-ci-success-prev',
    job_id: 'job-ci-refresh',
    status: 'success',
    started_at: new Date(Date.now() - 42 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 42 * 60 * 60 * 1000 + 840000).toISOString(),
    duration_seconds: 840,
    error_message: null,
  },
  {
    id: 'run-ci-success-prev2',
    job_id: 'job-ci-refresh',
    status: 'success',
    started_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 48 * 60 * 60 * 1000 + 790000).toISOString(),
    duration_seconds: 790,
    error_message: null,
  },
  {
    id: 'run-001',
    job_id: 'job-001',
    status: 'success',
    started_at: '2024-01-15T10:00:00Z',
    completed_at: '2024-01-15T10:15:30Z',
    duration_seconds: 930,
    error_message: null,
  },
  {
    id: 'run-002',
    job_id: 'job-002',
    status: 'success',
    started_at: '2024-01-15T09:30:00Z',
    completed_at: '2024-01-15T09:42:15Z',
    duration_seconds: 735,
    error_message: null,
  },
  {
    id: 'run-003',
    job_id: 'job-003',
    status: 'failed',
    started_at: '2024-01-15T08:15:00Z',
    completed_at: '2024-01-15T08:18:45Z',
    duration_seconds: 225,
    error_message: 'Connection timeout to external API',
  },
  {
    id: 'run-004',
    job_id: 'job-004',
    status: 'running',
    started_at: '2024-01-15T11:00:00Z',
    completed_at: null,
    duration_seconds: null,
    error_message: null,
  },
  {
    id: 'run-005',
    job_id: 'job-005',
    status: 'success',
    started_at: '2024-01-15T07:00:00Z',
    completed_at: '2024-01-15T07:12:20Z',
    duration_seconds: 740,
    error_message: null,
  },
  {
    id: 'run-006',
    job_id: 'job-006',
    status: 'failed',
    started_at: '2024-01-15T06:00:00Z',
    completed_at: '2024-01-15T06:05:10Z',
    duration_seconds: 310,
    error_message: 'Data validation failed: missing required fields',
  },
  {
    id: 'run-007',
    job_id: 'job-001',
    status: 'success',
    started_at: '2024-01-14T10:00:00Z',
    completed_at: '2024-01-14T10:16:45Z',
    duration_seconds: 1005,
    error_message: null,
  },
  {
    id: 'run-008',
    job_id: 'job-002',
    status: 'running',
    started_at: '2024-01-15T11:30:00Z',
    completed_at: null,
    duration_seconds: null,
    error_message: null,
  },
]);

// ==================== Events Mock Data ====================

export const mockEventsOverview = {
  total_events: 236,
  success_rate: 88.1,
  failed_jobs: 9,
  running_jobs: 5,
};

export const mockEvents = enrichWithWorkload([
  {
    id: 'evt-ci-sla',
    job_id: 'job-ci-refresh',
    dataset_id: 'ds-critical-001',
    event_type: 'SLA Breach',
    status: 'WARNING',
    description: 'Dataset customer-interactions-v3 freshness SLA breached: 36h stale (SLA: 6h). Downstream AI systems affected.',
    created_at: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(),
    tags: ['sla', 'freshness', 'ai-critical', 'breach'],
  },
  {
    id: 'evt-ci-fail',
    job_id: 'job-ci-refresh',
    dataset_id: 'ds-critical-001',
    event_type: 'Job Failed',
    status: 'FAILED',
    description: 'Pipeline job customer_interactions_refresh failed: Source database connection refused (Redshift cluster ai-analytics-prod unreachable)',
    created_at: new Date(Date.now() - 36 * 60 * 60 * 1000 + 45000).toISOString(),
    tags: ['pipeline', 'failed', 'ai-critical', 'sla-breach'],
  },
  {
    id: 'evt-001',
    job_id: 'job-001',
    dataset_id: 'ds-001',
    event_type: 'Job Start',
    status: 'RUNNING',
    description: 'Pipeline job customer_data_pipeline started execution',
    created_at: '2024-01-15T10:00:00Z',
    tags: ['pipeline', 'start'],
  },
  {
    id: 'evt-002',
    job_id: 'job-002',
    dataset_id: 'ds-002',
    event_type: 'Job Complete',
    status: 'SUCCESS',
    description: 'Pipeline job order_processing completed successfully',
    created_at: '2024-01-15T09:42:15Z',
    tags: ['pipeline', 'complete', 'success'],
  },
  {
    id: 'evt-003',
    job_id: 'job-003',
    dataset_id: 'ds-003',
    event_type: 'Job Failed',
    status: 'FAILED',
    description: 'Pipeline job product_sync failed: Connection timeout to external API',
    created_at: '2024-01-15T08:18:45Z',
    tags: ['pipeline', 'failed', 'error'],
  },
  {
    id: 'evt-004',
    job_id: 'job-004',
    dataset_id: 'ds-004',
    event_type: 'Data Quality',
    status: 'WARNING',
    description: 'Data quality check detected anomalies in user_sessions dataset',
    created_at: '2024-01-15T11:20:00Z',
    tags: ['quality', 'warning', 'anomaly'],
  },
  {
    id: 'evt-005',
    job_id: null,
    dataset_id: 'ds-001',
    event_type: 'Lineage Update',
    status: 'SUCCESS',
    description: 'Lineage graph updated for customer_profiles dataset',
    created_at: '2024-01-15T10:30:00Z',
    tags: ['lineage', 'update'],
  },
  {
    id: 'evt-006',
    job_id: 'job-005',
    dataset_id: 'ds-005',
    event_type: 'Job Start',
    status: 'RUNNING',
    description: 'Pipeline job campaign_metrics started execution',
    created_at: '2024-01-15T07:00:00Z',
    tags: ['pipeline', 'start'],
  },
  {
    id: 'evt-007',
    job_id: 'job-006',
    dataset_id: 'ds-006',
    event_type: 'Job Failed',
    status: 'FAILED',
    description: 'Pipeline job financial_reconciliation failed: Data validation failed',
    created_at: '2024-01-15T06:05:10Z',
    tags: ['pipeline', 'failed', 'validation'],
  },
  {
    id: 'evt-008',
    job_id: 'job-001',
    dataset_id: 'ds-001',
    event_type: 'Job Complete',
    status: 'SUCCESS',
    description: 'Pipeline job customer_data_pipeline completed successfully',
    created_at: '2024-01-15T10:15:30Z',
    tags: ['pipeline', 'complete', 'success'],
  },
]);

// ==================== Impact Mock Data ====================
// Story: customer-interactions-v3 impacts a GenAI prompt and an agent (Act 3, Step 8)

export const mockImpactAnalysis = {
  overview: {
    downstream_jobs: 1,
    downstream_datasets: 1,
    affected_users: 2400,
    critical_paths: 2,
    impact_level: 'Critical',
  },
  message: 'Stale data in customer-interactions-v3 is actively degrading AI recommendation quality and causing unsafe agent behavior. Immediate remediation required.',
  dependencies: [
    {
      id: 'dep-001',
      name: 'product-recommendation',
      dependency_type: 'Prompt',
      criticality: 'CRITICAL',
      users_count: 2200,
    },
    {
      id: 'dep-002',
      name: 'customer-support-agent',
      dependency_type: 'Agent',
      criticality: 'HIGH',
      users_count: 180,
    },
    {
      id: 'dep-003',
      name: 'ai_feature_pipeline',
      dependency_type: 'Job',
      criticality: 'HIGH',
      users_count: 0,
    },
    {
      id: 'dep-004',
      name: 'feature-store-v2',
      dependency_type: 'Dataset',
      criticality: 'MEDIUM',
      users_count: 15,
    },
  ],
};

export const mockImpactOverview = {
  downstream_jobs: 1,
  downstream_datasets: 1,
  affected_users: 2400,
  critical_paths: 2,
  impact_level: 'Critical',
};

export const mockDependencies = [
  {
    id: 'dep-001',
    name: 'product-recommendation',
    dependency_type: 'Prompt',
    criticality: 'CRITICAL',
    users_count: 2200,
  },
  {
    id: 'dep-002',
    name: 'customer-support-agent',
    dependency_type: 'Agent',
    criticality: 'HIGH',
    users_count: 180,
  },
  {
    id: 'dep-003',
    name: 'ai_feature_pipeline',
    dependency_type: 'Job',
    criticality: 'HIGH',
    users_count: 0,
  },
  {
    id: 'dep-004',
    name: 'feature-store-v2',
    dependency_type: 'Dataset',
    criticality: 'MEDIUM',
    users_count: 15,
  },
];

// ==================== Helper Functions ====================

/**
 * Simulate API delay for realistic mock behavior
 */
export const delay = (ms: number = 300): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Filter datasets based on query parameters
 */
export const filterDatasets = (datasets: typeof mockDatasets, filters: Record<string, any> = {}): typeof mockDatasets => {
  let filtered = [...datasets];

  if (filters.project_id) {
    filtered = filtered.filter(d => d.project_id === filters.project_id);
  }

  if (filters.workload_id) {
    filtered = filtered.filter(d => {
      const wl = PROJECT_TO_WORKLOAD[(d as any).project_id];
      return wl && wl.id === filters.workload_id;
    });
  }

  if (filters.account_id) {
    filtered = filtered.filter(d => (d as any).account_id === filters.account_id || (d as any).cloud_account_id === filters.account_id);
  }

  if (filters.environment_id) {
    filtered = filtered.filter(d => (d as any).environment_id === filters.environment_id);
  }

  if (filters.namespace) {
    filtered = filtered.filter(d => d.namespace === filters.namespace);
  }

  if (filters.source) {
    filtered = filtered.filter(d => (d as any).source === filters.source);
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(d =>
      d.name.toLowerCase().includes(searchLower) ||
      d.description?.toLowerCase().includes(searchLower)
    );
  }

  return filtered;
};

/**
 * Filter jobs based on query parameters
 */
export const filterJobs = (jobs: typeof mockJobs, filters: Record<string, any> = {}): typeof mockJobs => {
  let filtered = [...jobs];

  if (filters.project_id) {
    filtered = filtered.filter(j => j.project_id === filters.project_id);
  }

  if (filters.workload_id) {
    filtered = filtered.filter(j => {
      const wl = PROJECT_TO_WORKLOAD[(j as any).project_id];
      return wl && wl.id === filters.workload_id;
    });
  }

  if (filters.account_id) {
    filtered = filtered.filter(j => (j as any).account_id === filters.account_id || (j as any).cloud_account_id === filters.account_id);
  }

  if (filters.environment_id) {
    filtered = filtered.filter(j => (j as any).environment_id === filters.environment_id);
  }

  if (filters.namespace) {
    filtered = filtered.filter(j => j.namespace === filters.namespace);
  }

  if (filters.status) {
    filtered = filtered.filter(j => j.status === filters.status);
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(j =>
      j.name.toLowerCase().includes(searchLower) ||
      j.description?.toLowerCase().includes(searchLower)
    );
  }

  return filtered;
};

/**
 * Filter runs based on query parameters
 */
export const filterRuns = (runs: typeof mockRuns, filters: Record<string, any> = {}): typeof mockRuns => {
  let filtered = [...runs];

  if (filters.job_id) {
    filtered = filtered.filter(r => r.job_id === filters.job_id);
  }

  if (filters.status) {
    filtered = filtered.filter(r => r.status === filters.status);
  }

  if (filters.start_date) {
    const startDate = new Date(filters.start_date);
    filtered = filtered.filter(r => new Date(r.started_at) >= startDate);
  }

  if (filters.end_date) {
    const endDate = new Date(filters.end_date);
    filtered = filtered.filter(r => new Date(r.started_at) <= endDate);
  }

  if (filters.limit) {
    filtered = filtered.slice(0, filters.limit);
  }

  return filtered;
};

/**
 * Filter events based on query parameters
 */
export const filterEvents = (events: typeof mockEvents, filters: Record<string, any> = {}): typeof mockEvents => {
  let filtered = [...events];

  if (filters.job_id) {
    filtered = filtered.filter(e => e.job_id === filters.job_id);
  }

  if (filters.dataset_id) {
    filtered = filtered.filter(e => e.dataset_id === filters.dataset_id);
  }

  if (filters.event_type) {
    filtered = filtered.filter(e => e.event_type === filters.event_type);
  }

  if (filters.status) {
    filtered = filtered.filter(e => e.status === filters.status);
  }

  if (filters.start_date) {
    const startDate = new Date(filters.start_date);
    filtered = filtered.filter(e => new Date(e.created_at) >= startDate);
  }

  if (filters.end_date) {
    const endDate = new Date(filters.end_date);
    filtered = filtered.filter(e => new Date(e.created_at) <= endDate);
  }

  if (filters.limit) {
    filtered = filtered.slice(0, filters.limit);
  }

  return filtered;
};

// Export all mock data and helpers
const dataopsMockData = {
  catalog: {
    overview: mockCatalogOverview,
    datasets: mockDatasets,
    jobs: mockJobs,
    namespaces: mockNamespaces,
    qualityResults: mockQualityResults,
  },
  lineage: {
    graph: mockLineageGraph,
    upstream: mockUpstreamNodes,
    downstream: mockDownstreamNodes,
    path: mockLineagePath,
  },
  runs: {
    overview: mockRunsOverview,
    runs: mockRuns,
  },
  events: {
    overview: mockEventsOverview,
    events: mockEvents,
  },
  impact: {
    analysis: mockImpactAnalysis,
    overview: mockImpactOverview,
    dependencies: mockDependencies,
  },
  helpers: {
    delay,
    filterDatasets,
    filterJobs,
    filterRuns,
    filterEvents,
  },
};
export default dataopsMockData;
